import * as React from 'react';
import type { JSX } from 'react';

import { CompactSizeDetailCard } from './components/variants/compact';
import { DefaultSizeDetailCard } from './components/variants/default';
import type { DetailCardProps } from './types';
import { Card, cn } from '../../v2';
import { StyledCardRibbon } from '../card-ribbon';
import { Tooltip } from '../tooltip';

export default function DetailCardV2(props: DetailCardProps): JSX.Element {
  const {
    actions,
    cardProps = {},
    children,
    defaultExpanded = false,
    eyebrowProps,
    onClick,
    selectableOptions,
    isHighlighted,
    isExpandable: isExpandableProp = true,
    ribbon,
    ribbonVariant,
    testId,
    disabledProps,
  } = props;
  const coalescedDisabledProps = disabledProps || { isDisabled: false };

  const [expanded, setExpanded] = React.useState(defaultExpanded);

  const { cardClassName, variant = 'default', highlightProps } = cardProps;

  const { cardClassName: highlightCardClassName } = highlightProps ?? {};

  const hasActions = actions && actions.length > 0;
  const hasChildren = !!children && React.Children.toArray(children).filter(Boolean).length > 0;
  const hasEyebrow = (eyebrowProps?.text?.length ?? 0) > 0;
  const isExpandable = isExpandableProp && hasChildren;
  const isSelectable = !!selectableOptions;
  const isClickable = !!onClick;

  const handleCardClick = (event: React.MouseEvent) => {
    if (!isClickable || coalescedDisabledProps.isDisabled) return;
    onClick?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isClickable || coalescedDisabledProps.isDisabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.(event as unknown as React.MouseEvent);
    }
  };

  const cardContent = (
    <Card
      className={cn(
        'bg-background shadow-brand-400 hover:bg-steel-50 dark:hover:bg-steel-500 relative overflow-hidden p-3 shadow-none',
        cardClassName,
        {
          'cursor-pointer': isClickable && !coalescedDisabledProps.isDisabled,
          'before:hidden': !isHighlighted,
          [cn(
            'bg-brand-100 before:bg-brand-400 dark:bg-steel-500 before:absolute before:inset-y-0 before:left-0 before:w-[6px]',
            highlightCardClassName,
          )]: isHighlighted,
        },
      )}
      data-testid={testId ?? 'detail-card'}
    >
      {variant === 'default' && (
        <DefaultSizeDetailCard
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          isHighlighted={isHighlighted}
          isSelectable={isSelectable}
          expanded={expanded}
          setExpanded={setExpanded}
          isExpandable={isExpandable}
          hasActions={hasActions ?? false}
          hasEyebrow={hasEyebrow}
          isClickable={isClickable}
          testId={testId}
          disabledProps={coalescedDisabledProps}
        />
      )}
      {variant === 'compact' && (
        <CompactSizeDetailCard
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          isHighlighted={isHighlighted}
          isSelectable={isSelectable}
          expanded={expanded}
          setExpanded={setExpanded}
          isExpandable={isExpandable}
          hasActions={hasActions ?? false}
          hasEyebrow={hasEyebrow}
          isClickable={isClickable}
          testId={testId}
          disabledProps={coalescedDisabledProps}
        />
      )}
      {ribbon && ribbonVariant && <StyledCardRibbon $variant={ribbonVariant}>{ribbon}</StyledCardRibbon>}
    </Card>
  );

  const card = isClickable ? (
    <div
      role="button"
      tabIndex={coalescedDisabledProps.isDisabled ? -1 : 0}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      className="focus-visible:ring-brand-400 rounded-sm outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2"
    >
      {cardContent}
    </div>
  ) : (
    cardContent
  );

  const { isDisabled, disabledMessage, disabledTooltipProps } = coalescedDisabledProps;
  if (isDisabled) {
    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <Tooltip message={disabledMessage} {...disabledTooltipProps}>
        <div className="relative cursor-not-allowed opacity-40">{card}</div>
      </Tooltip>
    );
  }
  return card;
}
